import React from "react";

import { Card, Col, Form, Row } from "react-bootstrap";

import ContactFormReferenceSection from "@/components/contact/elements/ContactFormReference.component";
import { yesNoOptions } from "@/context/ContactProvider.context";
import { getCurrentLang } from "@/utils/Local";
import { formatFormOptionValue } from "@/utils/Toolbox";

import useContactContext from "../../../hooks/useContactContext";
import i18n from "../../../i18n";
import ContactFormLocationSection from "../elements/ContactFormLocationSection.component";
import ContactFormPhoneSection from "../elements/ContactFormPhoneSection.component";
import ContactFormProjectInfoSection from "../elements/ContactFormProjectInfoSection.component";

const ContactFormHomeowners = () => {
  const { t } = i18n;
  const { onSubmit, methods, submitting } = useContactContext();

  methods.watch("hasSpecificProject");
  return (
    <Form className="w-100" onSubmit={methods.handleSubmit(onSubmit)}>
      <fieldset disabled={submitting}>
        <input
          type="hidden"
          {...methods.register("kind")}
          label={null}
          value="homeowners"
        />
        <input
          type="hidden"
          {...methods.register("lang")}
          label={null}
          value={getCurrentLang()}
        />
        <Card>
          <Card.Body>
            <Card.Title>{t("Primary Information")}</Card.Title>

            <Row>
              <Col xs={12}>
                <Row>
                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>{t("Full name")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...methods.register("name")}
                        isInvalid={!!methods.formState.errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.name?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>{t("Email")}</Form.Label>
                      <Form.Control
                        type="email"
                        {...methods.register("email")}
                        isInvalid={!!methods.formState.errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {methods.formState.errors.email?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <ContactFormPhoneSection
                      control={methods.control}
                      errors={methods.formState.errors}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Body>
            <Row>
              <Col xs={12}>
                <Form.Group
                  className="mb-3"
                  controlId="project-requires-financing"
                >
                  <Form.Label>
                    {t("Do you have a specific project in mind?")}
                  </Form.Label>
                  {yesNoOptions.map((v) => (
                    <Form.Check
                      key={`key-${v}`}
                      className="radio-pill"
                      id={`hasSpecificProject-${v}`}
                      label={t(formatFormOptionValue(v))}
                      type="radio"
                      value={v}
                      {...methods.register("hasSpecificProject", {
                        onChange: () => {
                          if (v === "yes") {
                            methods.setValue("note", undefined, {
                              shouldValidate: true,
                            });
                          } else {
                            methods.setValue("location", undefined, {
                              shouldValidate: true,
                            });
                            methods.setValue("hasLand", undefined, {
                              shouldValidate: true,
                            });
                            methods.setValue("projectInfo", undefined, {
                              shouldValidate: true,
                            });
                          }
                          methods.trigger([
                            "hasSpecificProject",
                            "projectInfo",
                            "location",
                            "note",
                          ]);
                        },
                      })}
                      isInvalid={!!methods.formState.errors.hasSpecificProject}
                      name="hasSpecificProject"
                    />
                  ))}
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {t(methods.formState.errors.hasSpecificProject?.message)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {methods.getValues("hasSpecificProject") === "yes" && (
          <React.Fragment>
            <Card className="mt-4">
              <Card.Body>
                <Row>
                  <Col xs={12}>
                    <ContactFormLocationSection methods={methods} />

                    <Row>
                      <Col lg={6} xs={12}>
                        <Form.Group className="mb-3" controlId="has-land">
                          <Form.Label>
                            {t("Do you currently own the land?")}
                          </Form.Label>
                          {yesNoOptions.map((v) => (
                            <Form.Check
                              key={`r-${v}`}
                              className="radio-pill"
                              id={`hasLand-${v}`}
                              label={t(formatFormOptionValue(v))}
                              type="radio"
                              value={v}
                              {...methods.register("hasLand", {
                                onChange: () => {
                                  methods.trigger("hasLand");
                                },
                              })}
                              isInvalid={!!methods.formState.errors.hasLand}
                              name="hasLand"
                            />
                          ))}
                          <Form.Control.Feedback
                            className="d-block"
                            type="invalid"
                          >
                            {t(methods.formState.errors.hasLand?.message)}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <ContactFormProjectInfoSection methods={methods} />
          </React.Fragment>
        )}

        {methods.getValues("hasSpecificProject") === "no" && (
          <Card className="mt-4">
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <Form.Group className="mb-3" controlId="note">
                    <Form.Label>
                      {t(
                        "Tell us more about your interest (ex: residential house, cottage, etc.)",
                      )}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      type="text"
                      {...methods.register("note")}
                      isInvalid={!!methods.formState.errors.note}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t(methods.formState.errors.note?.message)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
        <ContactFormReferenceSection methods={methods} />
      </fieldset>
    </Form>
  );
};

export default ContactFormHomeowners;
